import {
  Button,
  Checkbox,
  type CitySuggestion,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  type FullCV,
  Input,
} from '@cibli/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, MapPin } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import PictureCapture from '@/features/picture/components/PictureCapture';
import { useBooth } from '@/features/settings/hooks/useBooth';
import { fetchCitySuggestions, resolveBaseCountry, validateCityZipcode } from '@/services/address';

import { useValidateOnMount } from '../../hooks/useValidateOnMount';
import { useVocalFormUpdate } from '../../hooks/useVocalFormUpdate';
import { createPersonalInfoSchema } from '../../schemas/personalInfo.schema';

interface PersonalInfoFormProps {
  onSubmit: (data: FullCV) => void;
  initialData?: FullCV;
  onCancel: () => void;
}

const PersonalInfoForm: React.FC<PersonalInfoFormProps> = ({ onSubmit, initialData, onCancel }) => {
  const { t, i18n } = useTranslation();

  // Locale de base de l'appareil (GH-599 / plan 2026-10-21) : pilote la
  // recherche — la langue d'affichage peut changer sans influencer les
  // résultats. La LOCALE configurée (cabine ou région web) prime sur le pays
  // physique : une base es-MX cherche au Mexique depuis la France.
  const { booth, webRegionConfig } = useBooth();
  const countryHint = resolveBaseCountry({
    boothDefaultLocaleId: booth?.defaultLocaleId,
    webRegionDefaultLocale: webRegionConfig?.defaultLocale?.codeLong,
    webRegionCountry: webRegionConfig?.countryCode,
  });
  // Langue de l'interface complète (es-MX, en-GB…) : noms de lieux localisés
  // côté providers externes (repli mondial).
  const uiLang = i18n.language || 'fr';

  const personalInfoSchema = createPersonalInfoSchema(t);
  type PIInput = z.input<typeof personalInfoSchema>;
  type PIOutput = z.output<typeof personalInfoSchema>;

  const [suggestions, setSuggestions] = useState<CitySuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [isValidatingCity, setIsValidatingCity] = useState(false);
  const [cityValidationError, setCityValidationError] = useState<string | null>(null);
  // Guidance shown when no suggestion matches, or after selecting a city
  // without postal code (GH-598: explain what to do next, in French).
  const [bottomHint, setBottomHint] = useState<string | null>(null);

  const initialCity = initialData?.location || '';
  const [cityDisplay, setCityDisplay] = useState(initialCity);
  // Sens inverse : saisir un code postal propose les villes correspondantes.
  const [zipSuggestions, setZipSuggestions] = useState<CitySuggestion[]>([]);
  const [showZipSuggestions, setShowZipSuggestions] = useState(false);
  const [isLoadingZip, setIsLoadingZip] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const zipDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Sequence guard: a slow response must never overwrite a newer one.
  const suggestionsSeqRef = useRef(0);
  const zipSuggestionsSeqRef = useRef(0);
  const cityInputRef = useRef<HTMLInputElement>(null);
  const zipInputRef = useRef<HTMLInputElement>(null);
  // Plan 2026-10-21 : vérification indisponible → réessai (1), puis
  // « vérifiez vos saisies » (2), la 3e validation enregistre en NON VÉRIFIÉ
  // (jamais mis en cache, jamais présenté comme vérifié).
  const unavailableAttemptsRef = useRef(0);

  const form = useForm<PIInput, unknown, PIOutput>({
    resolver: zodResolver(personalInfoSchema),
    defaultValues: initialData
      ? {
          ...initialData,
          postalCode: initialData.postalCode ?? null,
          email: initialData.email || '',
          driverLicenseTypes: initialData.driverLicenseTypes ?? null,
          isMobile: initialData.isMobile ?? false,
          openToTraining: initialData.openToTraining ?? false,
          gender: initialData.gender ?? null,
        }
      : {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          hasDriverLicense: false,
          driverLicenseTypes: null,
          isMobile: false,
          openToTraining: false,
          gender: null,
          location: '',
          postalCode: null,
          jobTitle: '',
          pictureBase64: null,
        },
  });

  const handleCityInputChange = useCallback(
    (value: string) => {
      setCityDisplay(value);
      setCityValidationError(null);
      setBottomHint(null);

      // Saisie libre = la ville affichée ; le code postal est un champ séparé.
      form.setValue('location', value.trim(), { shouldValidate: true });
      unavailableAttemptsRef.current = 0;

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      const trimmed = value.trim();

      if (trimmed.length < 1) {
        suggestionsSeqRef.current += 1;
        setIsLoadingCities(false);
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      debounceRef.current = setTimeout(async () => {
        const seq = ++suggestionsSeqRef.current;
        setIsLoadingCities(true);
        try {
          const results = await fetchCitySuggestions(value, 15, countryHint, uiLang);
          // Ignore stale responses (a newer keystroke already started a fetch).
          if (seq !== suggestionsSeqRef.current) return;
          setSuggestions(results);
          setShowSuggestions(results.length > 0);
          if (results.length === 0 && trimmed.length >= 2) {
            setBottomHint(t('FormScreen.City.noSuggestions', { query: trimmed }));
          }
        } finally {
          if (seq === suggestionsSeqRef.current) {
            setIsLoadingCities(false);
          }
        }
      }, 350);
    },
    [form, countryHint, uiLang, t],
  );

  const handleSuggestionSelect = useCallback(
    (suggestion: CitySuggestion) => {
      suggestionsSeqRef.current += 1;
      unavailableAttemptsRef.current = 0;
      form.setValue('location', suggestion.city, { shouldValidate: true });
      form.setValue('postalCode', suggestion.zipcode, { shouldValidate: true });
      setCityDisplay(suggestion.city);
      setSuggestions([]);
      setShowSuggestions(false);
      setCityValidationError(null);
      // City without postal code: tell the user the postal code is still needed.
      setBottomHint(suggestion.zipcode ? null : t('FormScreen.City.addPostalCode'));
      cityInputRef.current?.blur();
    },
    [form, t],
  );

  const handleZipcodeInputChange = useCallback(
    (value: string) => {
      setCityValidationError(null);
      setBottomHint(null);
      form.setValue('postalCode', value.trim(), { shouldValidate: true });
      unavailableAttemptsRef.current = 0;

      if (zipDebounceRef.current) {
        clearTimeout(zipDebounceRef.current);
      }

      const trimmed = value.trim();
      if (trimmed.length < 2) {
        zipSuggestionsSeqRef.current += 1;
        setIsLoadingZip(false);
        setZipSuggestions([]);
        setShowZipSuggestions(false);
        return;
      }

      zipDebounceRef.current = setTimeout(async () => {
        const seq = ++zipSuggestionsSeqRef.current;
        setIsLoadingZip(true);
        try {
          const results = await fetchCitySuggestions(trimmed, 15, countryHint, uiLang);
          if (seq !== zipSuggestionsSeqRef.current) return;
          setZipSuggestions(results);
          setShowZipSuggestions(results.length > 0);
        } finally {
          if (seq === zipSuggestionsSeqRef.current) {
            setIsLoadingZip(false);
          }
        }
      }, 350);
    },
    [form, countryHint, uiLang],
  );

  const handleZipSuggestionSelect = useCallback(
    (suggestion: CitySuggestion) => {
      zipSuggestionsSeqRef.current += 1;
      unavailableAttemptsRef.current = 0;
      form.setValue('postalCode', suggestion.zipcode, { shouldValidate: true });
      form.setValue('location', suggestion.city, { shouldValidate: true });
      setCityDisplay(suggestion.city);
      setZipSuggestions([]);
      setShowZipSuggestions(false);
      setCityValidationError(null);
      setBottomHint(null);
      zipInputRef.current?.blur();
    },
    [form],
  );

  // Vrai = le candidat peut enregistrer avec un couple NON VÉRIFIÉ.
  const handleUnavailable = (): boolean => {
    const attempt = unavailableAttemptsRef.current;
    if (attempt === 0) {
      unavailableAttemptsRef.current = 1;
      setCityValidationError(t('formErrors.verifyUnavailableRetry'));
      return false;
    }
    if (attempt === 1) {
      unavailableAttemptsRef.current = 2;
      setCityValidationError(t('formErrors.verifyUnavailableCheck'));
      return false;
    }
    setCityValidationError(null);
    return true;
  };

  const handleSubmit = async (data: PIOutput) => {
    setCityValidationError(null);
    let city = data.location?.trim() || '';
    const zipcode = data.postalCode?.trim() || '';

    if (zipcode) {
      setIsValidatingCity(true);
      try {
        if (!city) {
          const fetchedSuggestions = await fetchCitySuggestions(zipcode, 1, countryHint, uiLang);
          if (fetchedSuggestions.length > 0) {
            // Ville tirée des suggestions du code postal : couple confirmé
            // par la source elle-même (aucune mise en cache).
            city = fetchedSuggestions[0].city;
            form.setValue('location', city);
            onSubmit({ ...data, location: city, postalCode: zipcode } as FullCV);
            return;
          }
          setCityValidationError(t('formErrors.invalidCityZipcode'));
          return;
        }

        const status = await validateCityZipcode(city, zipcode, countryHint);
        if (status === 'invalid') {
          setCityValidationError(t('formErrors.invalidCityZipcode'));
          return;
        }
        if (status === 'unavailable' && !handleUnavailable()) {
          return;
        }
      } catch (error) {
        console.error('City validation failed:', error);
        // Échec de requête : flux réessai → saisie manuelle, jamais de
        // passage silencieux (plan 2026-10-21).
        if (!handleUnavailable()) {
          return;
        }
      } finally {
        setIsValidatingCity(false);
      }
    }

    onSubmit({ ...data, location: city, postalCode: zipcode || null } as FullCV);
  };

  const handleImageCapture = (imageBase64: string | null) => {
    form.setValue('pictureBase64', imageBase64 || '');
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (zipDebounceRef.current) {
        clearTimeout(zipDebounceRef.current);
      }
    };
  }, []);

  useValidateOnMount(form);
  useVocalFormUpdate(form, 'personalInfos');

  return (
    <div className="container mx-auto p-4 max-w-2xl">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex flex-col gap-2 w-full items-center mb-4">
            <h3 className="text-lg text-primary">{t('Forms.title')}</h3>
            <h1 className="text-2xl font-bold">{t('Forms.PersonalInfo.title')}</h1>
          </div>
        </div>

        {/* Photo capture section */}
        <div className="flex flex-col items-center justify-center mb-6">
          <PictureCapture
            initialImage={form.getValues('pictureBase64') || null}
            onImageCapture={handleImageCapture}
            size="lg"
          />
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <div className="grid grid-cols-2 gap-4 pb-20">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t('Forms.PersonalInfo.firstName')}</FormLabel>
                    <FormControl>
                      <Input
                        className="h-12"
                        placeholder={t('Forms.PersonalInfo.firstNamePlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>{t('Forms.PersonalInfo.lastName')}</FormLabel>
                    <FormControl>
                      <Input
                        className="h-12"
                        placeholder={t('Forms.PersonalInfo.lastNamePlaceholder')}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Forms.PersonalInfo.email')}</FormLabel>
                  <FormControl>
                    <Input
                      className="h-12"
                      placeholder={t('Forms.PersonalInfo.emailPlaceholder')}
                      {...field}
                      value={field.value || ''}
                      onChange={e => {
                        if (e.target.value === '') {
                          field.onChange(null);
                        } else {
                          field.onChange(e.target.value);
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>{t('Forms.PersonalInfo.phone')}</FormLabel>
                  <FormControl>
                    <Input
                      className="h-12"
                      placeholder={t('Forms.PersonalInfo.phonePlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="location"
              render={() => (
                <FormItem className="relative">
                  <FormLabel required>{t('FormScreen.City.cityPlaceholder')}</FormLabel>
                  <div className="relative">
                    <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
                    <FormControl>
                      <Input
                        ref={cityInputRef}
                        className="h-12 pl-11"
                        placeholder={t('FormScreen.City.cityPlaceholder')}
                        value={cityDisplay}
                        onChange={e => handleCityInputChange(e.target.value)}
                        onFocus={() => {
                          if (suggestions.length > 0) setShowSuggestions(true);
                        }}
                        onBlur={() => {
                          form.setValue('location', cityDisplay.trim(), { shouldValidate: true });
                          setTimeout(() => setShowSuggestions(false), 200);
                        }}
                      />
                    </FormControl>
                    {isLoadingCities && (
                      <Loader2 className="absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                  </div>

                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {suggestions.map((suggestion, index) => (
                        <button
                          key={`${suggestion.city}-${suggestion.zipcode}-${index}`}
                          type="button"
                          className="w-full px-4 py-3 min-h-[48px] text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-100 last:border-b-0 cursor-pointer flex items-center justify-between"
                          onMouseDown={e => {
                            e.preventDefault();
                            handleSuggestionSelect(suggestion);
                          }}
                        >
                          <span className="font-medium text-sm text-foreground">
                            {suggestion.city}
                          </span>
                          {suggestion.zipcode && (
                            <span className="text-xs text-muted-foreground">
                              ({suggestion.zipcode})
                            </span>
                          )}
                          {suggestion.country && (
                            <span className="text-xs text-muted-foreground">
                              · {suggestion.country}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {cityValidationError && (
                    <p role="alert" className="text-sm font-medium text-destructive mt-1">
                      {cityValidationError}
                    </p>
                  )}
                  {!cityValidationError && bottomHint && (
                    <p className="text-sm text-muted-foreground mt-1">{bottomHint}</p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="postalCode"
              render={({ field }) => (
                <FormItem className="relative">
                  <FormLabel>{t('FormScreen.City.zipcodePlaceholder')}</FormLabel>
                  <div className="relative">
                    <FormControl>
                      <Input
                        className="h-12"
                        inputMode="numeric"
                        placeholder={t('FormScreen.City.zipcodePlaceholder')}
                        {...field}
                        ref={zipInputRef}
                        value={field.value ?? ''}
                        onChange={e => handleZipcodeInputChange(e.target.value)}
                        onFocus={() => {
                          if (zipSuggestions.length > 0) setShowZipSuggestions(true);
                        }}
                        onBlur={() => {
                          form.setValue('postalCode', (field.value ?? '').trim(), {
                            shouldValidate: true,
                          });
                          setTimeout(() => setShowZipSuggestions(false), 200);
                        }}
                      />
                    </FormControl>
                    {isLoadingZip && (
                      <Loader2 className="absolute right-3.5 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                  </div>

                  {showZipSuggestions && zipSuggestions.length > 0 && (
                    <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {zipSuggestions.map((suggestion, index) => (
                        <button
                          key={`zip-${suggestion.city}-${suggestion.zipcode}-${index}`}
                          type="button"
                          className="w-full px-4 py-3 min-h-[48px] text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none border-b border-gray-100 last:border-b-0 cursor-pointer flex items-center justify-between"
                          onMouseDown={e => {
                            e.preventDefault();
                            handleZipSuggestionSelect(suggestion);
                          }}
                        >
                          <span className="font-medium text-sm text-foreground">
                            {suggestion.city}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            ({suggestion.zipcode})
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="jobTitle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>{t('Forms.PersonalInfo.jobTitle')}</FormLabel>
                  <FormControl>
                    <Input
                      className="h-12"
                      placeholder={t('Forms.PersonalInfo.jobTitlePlaceholder')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="hasDriverLicense"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Forms.PersonalInfo.hasDriverLicense')}</FormLabel>
                  <FormControl>
                    <Checkbox
                      className="resize-none"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      name={field.name}
                      ref={field.ref}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {form.watch('hasDriverLicense') && (
              <FormField
                control={form.control}
                name="driverLicenseTypes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('Forms.PersonalInfo.driverLicenseTypes')}</FormLabel>
                    <FormControl>
                      <Input
                        className="h-12"
                        placeholder={t('Forms.PersonalInfo.driverLicenseTypesPlaceholder')}
                        {...field}
                        value={typeof field.value === 'string' ? field.value : ''}
                        onChange={e =>
                          field.onChange(e.target.value === '' ? null : e.target.value)
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="isMobile"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Forms.PersonalInfo.showMobilityOnCV')}</FormLabel>
                  <FormControl>
                    <Checkbox
                      className="resize-none"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      name={field.name}
                      ref={field.ref}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="openToTraining"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('Forms.PersonalInfo.openToTraining')}</FormLabel>
                  <FormControl>
                    <Checkbox
                      className="resize-none"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      name={field.name}
                      ref={field.ref}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-between gap-4 mt-6">
              <Button type="button" size="lg" variant="secondary" onClick={onCancel}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                size="lg"
                disabled={isValidatingCity}
                className="bg-primary text-white hover:bg-primary/90"
              >
                {isValidatingCity && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('common.save')}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
};

export default PersonalInfoForm;
